<script setup lang="ts">
import type { Media } from "@/models"
import type { IMenuItem, IReactionRequestV1 } from "@/types"
import { useQueryClient } from "@tanstack/vue-query"
import { onKeyStroke, useFullscreen, useStyleTag, useSwipe } from "@vueuse/core"
import { computed, onBeforeUnmount, onMounted, onUnmounted, ref, useTemplateRef, watch, watchEffect } from "vue"
import { useAddReaction, useGetEventVault } from "@/api"
import { MediaDeleteModal, MediaDetailsModal, MediaEditModal, ReactionButton } from "@/components"
import { Menu } from "@/components/legacy"
import { Button, Icon, Pagination } from "@/components/ui"
import { useLightbox, useMediaReaction, useModal, useTranslation } from "@/composables"
import { MediaType } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { MediaDeleteModalKey, MediaDetailsModalKey, MediaEditModalKey } from "@/symbols"
import { SocialsBehavior, TaggingBehavior } from "@/types"
import { safelyPlayVideo, safelyUnloadVideoElement } from "@/utils"
import { useToast } from "../ui/ToastReka/useToast"

interface LightboxProps {
  isHost?: boolean
  totalMediaCountDisplay?: number
  isFetching?: boolean
  isPinning?: boolean
}

const props = withDefaults(defineProps<LightboxProps>(), {
  isHost: false,
  totalMediaCountDisplay: undefined,
  isFetching: false,
  isPinning: false,
})

const emit = defineEmits<{
  (e: "favourite", media?: Media): void
  (e: "fetchMore"): void
  (e: "viewComments", mediaItem: Media): void
  (e: "flag", media?: Media): void
}>()

const eventVaultStore = useEventVaultStore()

const { t } = useTranslation()
const toast = useToast()
const queryClient = useQueryClient()
const lightboxMediaRef = useTemplateRef<HTMLElement | null>("lightboxMediaRef")

const { data: eventVault } = useGetEventVault(eventVaultStore.eventVaultId)

const {
  media,
  visible: isLightboxOpen,
  hide,
  pagination,
  startSlideshow,
  stopSlideshow,
  isSlideshowActive,
  handleMediaEnd,
} = useLightbox({
  beforeHide: () => {
    postReaction()
    queryClient.invalidateQueries({ queryKey: ["listMedia"] })
  },
})

const { load, unload } = useStyleTag("html, body { overflow: hidden; };")

const { toggle: toggleDeleteModal } = useModal(MediaDeleteModalKey)
const { toggle: toggleDetailsModal } = useModal(MediaDetailsModalKey)
const { toggle: toggleEditModal } = useModal(MediaEditModalKey)

watchEffect(() => {
  if (isLightboxOpen.value)
    load()
  else unload()
})

const currentMedia = computed<Media | undefined>(() => {
  return media.value[pagination.page.value - 1]
})

const currentVideoRef = useTemplateRef<HTMLVideoElement | null>("currentVideoRef")

// Videos rely on a JS-driven `play()` call (instead of the `autoplay`
// attribute) so a rejected play request can be caught instead of surfacing
// as an unhandled `InvalidStateError`/`NotAllowedError` on iOS. Watching the
// template ref itself (rather than `currentMedia.id` + `nextTick`) ensures
// this fires once Vue actually mounts the new element, even though the
// `out-in` transition on the media wrapper delays that mount.
watch(currentVideoRef, (video) => {
  if (!video)
    return

  safelyPlayVideo(video, () => {
    // Only skip ahead if this is still the current video - avoids
    // double-advancing if pagination already moved on (e.g. `play()` was
    // aborted by `safelyUnloadVideoElement`'s `load()` call on the outgoing
    // video) before this rejection was reported.
    if (isSlideshowActive.value && currentVideoRef.value === video)
      handleMediaEnd()
  })
})

// Clean up the outgoing video element before it is swapped out for the next
// slide, or removed when the lightbox is closed. This runs before Vue
// re-renders (default "pre" watch flush), so `currentVideoRef.value` still
// points at the element about to be replaced/removed. See Slideshow.vue for
// the same iOS Safari `InvalidStateError` mitigation.
watch(() => currentMedia.value?.id, () => {
  safelyUnloadVideoElement(currentVideoRef.value)
})

onBeforeUnmount(() => {
  safelyUnloadVideoElement(currentVideoRef.value)
})

function onFavourite(media?: Media) {
  if (!props.isHost)
    return
  emit("favourite", media)
}

const {
  isFullscreen,
  toggle: toggleFullScreen,
  exit: exitFullScreen,
} = useFullscreen(lightboxMediaRef, { autoExit: true })

watchEffect(() => {
  if (!isFullscreen.value) {
    exitFullScreen().catch(() => {})
    stopSlideshow()
  }
})

const showCloseSlideshowButton = ref<boolean>(false)
let hideCloseButtonTimeout: ReturnType<typeof setTimeout> | null = null

function handleSlideshowMouseMove() {
  showCloseSlideshowButton.value = true
  if (hideCloseButtonTimeout !== null) {
    clearTimeout(hideCloseButtonTimeout)
  }
  hideCloseButtonTimeout = setTimeout(() => {
    showCloseSlideshowButton.value = false
    hideCloseButtonTimeout = null
  }, 5000)
}

onUnmounted(() => {
  exitFullScreen().catch(() => {})
  stopSlideshow()
})

function handleKeyStroke(key: string, action: () => void) {
  onKeyStroke(
    key,
    () => {
      if (!isLightboxOpen.value)
        return
      action()
    },
    { dedupe: true },
  )
}

function paginateNext() {
  pagination.next()
  postReaction()
}

function paginatePrevious() {
  pagination.previous()
  postReaction()
}

handleKeyStroke("ArrowRight", paginateNext)
handleKeyStroke("ArrowLeft", paginatePrevious)
useSwipe(lightboxMediaRef, {
  onSwipeEnd(_, direction) {
    if (direction === "left")
      paginateNext()
    if (direction === "right")
      paginatePrevious()
  },
})

const moreActionItems = computed(() => {
  const defaultActionItems: Array<IMenuItem> = [
    {
      key: "slideshow",
      label: t("lightbox.start_slideshow"),
      onClick: () => {
        toggleFullScreen().catch(() => {})
        startSlideshow()
      },
      dismissOnInteract: true,
    },
    {
      key: "flag",
      label: t("lightbox.flag_media"),
      onClick: () => emit("flag", currentMedia.value),
      dismissOnInteract: true,
    },
  ]

  // allow downloads for hosts, if downloads are enabled for guests or if the current user owns the media
  if (props.isHost || eventVault.value?.eventVaultSettings?.allowGuestDownload || currentMedia.value?.isPrivileged) {
    defaultActionItems.push({
      key: "download",
      label: t("download"),
      onClick: () => window.open(currentMedia.value?.downloadUrl, "_blank"),
      dismissOnInteract: true,
    })
  }

  const privilegedActionItems: Array<IMenuItem> = [
    {
      key: "edit",
      label: t("edit"),
      onClick: toggleEditModal,
      dismissOnInteract: true,
    },
    {
      key: "delete",
      label: t("delete"),
      onClick: toggleDeleteModal,
      dismissOnInteract: true,
    },
  ]

  return currentMedia.value?.isPrivileged ? [...privilegedActionItems, ...defaultActionItems] : defaultActionItems
})

const areReactionsEnabled = computed(() => {
  const socialsBehavior = eventVault.value?.eventVaultSettings?.socialsBehavior
  return (socialsBehavior === SocialsBehavior.ReactionsOnly || socialsBehavior === SocialsBehavior.ReactionsAndComments)
})

const areCommentsEnabled = computed(() => {
  const socialsBehavior = eventVault.value?.eventVaultSettings?.socialsBehavior

  return (socialsBehavior === SocialsBehavior.CommentsOnly || socialsBehavior === SocialsBehavior.ReactionsAndComments)
})

const superReactCount = 10
const ReactButtonRef = ref<InstanceType<typeof ReactionButton> | null>(null)

const {
  incrementedReactionCount,
  currentReactionCount,
  hasUserReacted,
  mediaId: currentMediaIdToReact,
  mediaType: currentMediaTypeToReact,
  pendingReactions,
  incrementTotalReactCount,
  clearReactionData,
} = useMediaReaction(currentMedia)

const { mutate: mutateReaction } = useAddReaction()

onMounted(() => {
  if (!eventVaultStore.eventVaultId || !pendingReactions || !areReactionsEnabled.value) {
    return
  }

  mutateReaction({ reaction: pendingReactions, eventVaultId: eventVaultStore.eventVaultId })
  clearReactionData()
})

function postReaction() {
  if (!eventVaultStore.eventVaultId || !incrementedReactionCount.value || !areReactionsEnabled.value) {
    return
  }

  const reaction: IReactionRequestV1 = {
    contentID: currentMediaIdToReact.value,
    contentType: currentMediaTypeToReact.value,
    type: "love",
    quantity: incrementedReactionCount.value,
  }

  mutateReaction({ reaction, eventVaultId: eventVaultStore.eventVaultId }, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["listMedia", eventVaultStore.eventVaultId] })
    },
  })

  clearReactionData()
}

async function onDeleteSuccess(_?: Media) {
  toast.success({
    title: t("success"),
    description: t("media_delete_modal.success"),
    icon: "CircleCheck",
  })

  await queryClient.invalidateQueries({ queryKey: ["listMedia", eventVaultStore.eventVaultId] })

  if (pagination.canPrevious) {
    pagination.previous()
  }
}

function onDeleteError() {
  toast.error()
}

const isFetchingNextPage = computed(() => {
  const currentPage = pagination.page.value
  const totalMediaItems = media.value.length

  return props.isFetching && currentPage === totalMediaItems
})

watchEffect(() => {
  const currentPage = pagination.page.value
  const totalMediaItems = media.value.length
  const threshold = 10

  const nearEnd = currentPage >= totalMediaItems - threshold

  if (nearEnd && !props.isFetching && isLightboxOpen.value) {
    emit("fetchMore")
  }
})
</script>

<template>
  <div v-if="isLightboxOpen" class="gpx-lightbox z-40">
    <div class="gpx-lightbox-scrim" />
    <div class="container gpx-lightbox-content z-50">
      <div v-if="!isSlideshowActive" class="gpx-lightbox-header">
        <button
          v-if="isHost"
          :disabled="!isHost || isPinning"
          class="gpx-lightbox-icon-button"
          @click="
            () => {
              postReaction();
              onFavourite(currentMedia);
            }
          "
        >
          <Icon v-if="isPinning" icon="Loading" />
          <Icon
            v-else
            :icon="currentMedia?.isPinned ? 'StarSolid' : 'Star'"
            :class="{
              'gpx-lightbox-icon_pinned': currentMedia?.isPinned,
              'gpx-lightbox-icon_guest-view': !isHost,
            }"
          />
        </button>
        <div />
        <button class="gpx-lightbox-icon-button" @click="hide">
          <span class="sr-only">{{ t("close") }}</span>
          <Icon icon="Close" />
        </button>
      </div>
      <div
        ref="lightboxMediaRef"
        class="gpx-lightbox-media"
        :class="{ 'gpx-lightbox-media_isFullscreen': isSlideshowActive }"
        role="presentation"
        @mousemove="handleSlideshowMouseMove"
        @dblclick="
          () => {
            if (areReactionsEnabled) {
              ReactButtonRef?.startAnimation();
              incrementTotalReactCount();
            }
          }
        "
      >
        <Button
          v-if="isSlideshowActive && showCloseSlideshowButton"
          class="gpx-lightbox-media-close-slideshow-button"
          variant="primary"
          @click="
            () => {
              exitFullScreen().catch(() => {});
              stopSlideshow();
            }
          "
        >
          <Icon icon="Close" />
          <span class="sr-only">{{ t("close") }}</span>
        </Button>
        <Transition name="gpx-fade" mode="out-in">
          <div
            :key="currentMedia?.id"
            class="flex flex-col items-center justify-center h-full"
          >
            <video
              v-if="currentMedia?.type === MediaType.Vid"
              :id="`gpx-gallery-video-${currentMedia?.id}`"
              ref="currentVideoRef"
              playsinline
              :poster="currentMedia?.getThumbnailUrl()"
              controls
              controlsList="nodownload"
              @contextmenu.prevent
            >
              <source :src="currentMedia.thumbnailUrl" type="video/mp4">
            </video>
            <picture v-else @contextmenu.prevent>
              <img :src="currentMedia?.getThumbnailUrl()" alt="">
            </picture>
            <div v-if="!isSlideshowActive" class="gpx-lightbox-details_mobile">
              <div class="flex items-center justify-between">
                <h2 v-if="currentMedia?.title">
                  {{ currentMedia.title }}
                </h2>
                <Button
                  class="gpx-details-modal-activator"
                  variant="flat"
                  @click="
                    () => {
                      postReaction();
                      toggleDetailsModal();
                    }
                  "
                >
                  <span class="sr-only">{{ t("lightbox.show_details") }}</span>
                  <Icon icon="CircleInfo" />
                </Button>
              </div>
              <p v-if="currentMedia?.uploadedBy">
                {{ t("uploaded_by") }} {{ currentMedia.uploadedBy }}
              </p>
            </div>
          </div>
        </Transition>
      </div>
      <div
        v-if="!isSlideshowActive"
        class="gpx-lightbox-footer"
        unselectable="on"
      >
        <div class="gpx-lightbox-details_desktop">
          <div class="gpx-lightbox-details-upper_desktop">
            <h2 v-if="currentMedia?.title">
              {{ currentMedia.title }}
            </h2>
            <Button
              class="gpx-details-modal-activator"
              variant="flat"
              @click="
                () => {
                  postReaction();
                  toggleDetailsModal();
                }
              "
            >
              <span class="sr-only">{{ t("lightbox.show_details") }}</span>
              <Icon icon="CircleInfo" />
            </Button>
          </div>
          <p v-if="currentMedia?.uploadedBy">
            {{ t("uploaded_by") }} {{ currentMedia.uploadedBy }}
          </p>
        </div>
        <Pagination
          variant="simple"
          :current-page="pagination.page.value"
          :page-count="totalMediaCountDisplay ?? media.length"
          :can-previous="pagination.canPrevious.value"
          :can-next="pagination.canNext.value"
          :is-fetching="isFetchingNextPage"
          @next="() => paginateNext()"
          @previous="() => paginatePrevious()"
        />
        <div class="gpx-lightbox-footer-actions">
          <ReactionButton
            v-if="areReactionsEnabled"
            ref="ReactButtonRef"
            data-test="gpx-lightbox-reaction-button"
            type="love"
            :total-react-count="currentReactionCount"
            :user-has-reacted="hasUserReacted"
            @on-react="() => incrementTotalReactCount()"
            @on-super-react="() => incrementTotalReactCount(superReactCount)"
          />

          <button
            v-if="areCommentsEnabled"
            data-test="gpx-lightbox-comment-button"
            class="gpx-lightbox-icon-button"
            @click="
              () => {
                postReaction();
                emit('viewComments', currentMedia!);
              }
            "
          >
            <Icon icon="ChatText" />
            {{ currentMedia?.totalComments }}
          </button>

          <Menu
            class="gpx-lightbox-more-button"
            strategy="fixed"
            :items="moreActionItems"
          >
            <template #activator="{ toggle: toggleMoreMenu }">
              <button
                data-test="gpx-lightbox-more-button"
                class="gpx-lightbox-icon-button"
                @click="
                  () => {
                    postReaction();
                    toggleMoreMenu();
                  }
                "
              >
                <Icon icon="MenuEllipsisHorizontal" />
                {{ t("more") }}
              </button>
            </template>
          </Menu>
        </div>
      </div>
    </div>
  </div>

  <MediaEditModal v-if="currentMedia" :media="currentMedia" />
  <MediaDetailsModal
    v-if="currentMedia"
    :media="currentMedia"
    :is-tagging-enabled="
      eventVault?.eventVaultSettings?.taggingBehavior === TaggingBehavior.On
    "
  />
  <MediaDeleteModal
    v-if="currentMedia"
    :media="[currentMedia]"
    @success="onDeleteSuccess"
    @error="onDeleteError"
  />
</template>

<style scoped>
h2,
p {
  padding: 0;
  margin: 0;
}

.gpx-lightbox {
  width: 100%;
  height: 100vh;
  height: 100dvh;
  position: fixed;
  inset: 0;
  color: white;
}

.gpx-lightbox-scrim {
  background-color: black;
  position: absolute;
  inset: 0;
}

.gpx-lightbox-icon-button {
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  font-size: 0.75rem;
}

.gpx-lightbox-header,
.gpx-lightbox-footer {
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
}

.gpx-lightbox-details_desktop {
  display: none;
}

.gpx-lightbox-details_mobile {
  margin-top: 1rem;
  text-align: start;
  width: 100%;
}

.gpx-lightbox-details-upper_desktop {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.gpx-lightbox-details-upper_desktop h2 {
  margin-right: 1rem;
}

.gpx-lightbox-details_desktop,
.gpx-lightbox-footer-actions {
  font-size: 0.75rem;
  flex: 1 0 0;
  touch-action: manipulation;
}

.gpx-lightbox-details_mobile h2 {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.gpx-lightbox-details_mobile p {
  font-size: 0.875rem;
}

.gpx-lightbox-details_desktop h2 {
  font-size: 1.135rem;
}

.gpx-lightbox-details_mobile h2,
.gpx-lightbox-details_mobile p,
.gpx-lightbox-details_desktop h2,
.gpx-lightbox-details_desktop p {
  max-width: 15rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.gpx-lightbox-footer-actions {
  display: flex;
  justify-content: end;
  gap: 1rem;
}

.gpx-lightbox-content {
  position: relative;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
}

.gpx-lightbox-media {
  flex: 1 1 auto;
}

.gpx-lightbox-media picture,
.gpx-lightbox-media video {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gpx-lightbox-media img,
.gpx-lightbox-media video {
  max-height: 60vh;
  max-width: 100%;
  object-fit: contain;
}

.gpx-lightbox-media_isFullscreen img,
.gpx-lightbox-media_isFullscreen video {
  max-height: 100vh;
  max-width: 100vw;
}

.gpx-lightbox-media-close-slideshow-button {
  position: fixed;
  top: 0.5rem;
  right: 0.5rem;
}

.gpx-lightbox-icon_pinned {
  color: gold;
}

.gpx-lightbox-icon_guest-view {
  cursor: default;
}

.gpx-details-modal-activator {
  padding: 0;
}

.gpx-details-modal-activator svg {
  color: white;
}

.gpx-lightbox-footer {
  user-select: none;
}

@media (width >= 768px) {
  .gpx-lightbox-details_desktop {
    display: block;
  }

  .gpx-lightbox-details_mobile {
    display: none;
  }

  .gpx-lightbox-media img {
    max-height: 75vh;
  }

  .gpx-lightbox-media_isFullscreen img,
  .gpx-lightbox-media_isFullscreen video {
    max-height: 100vh;
    max-width: 100vw;
  }
}
</style>
